import * as React from "react";
import "./AddressAutoComplete.css";
import "../../styles/FormInput.css";
import "../../styles/Buttons.css";
import Client from "../../services/Client";
import reducer, { initialState } from "./LocalReducer";
import { useReducer, useEffect, useRef } from "react";
import {
    changeSearchStatusValue,
    onAddressInputFocus,
    onLoadItemListFromRequest,
    onItemListClick,
    onCloseButton
} from "./LocalActions";
import { useDebounceHook } from "./Hooks";
import { ParsedAddress } from "../../dtos/ParsedAddress";
import { CountryCodes } from "../../constants/countries";
const rightCaret = require('../../assets/images/topbar_right_arrow.png');

interface IAddressAutoCompleteProps {
    id: string;
    isValid?: boolean;
    placeholder?: string;
    validationMessage?: string;
    countryCode: CountryCodes;
    langCode: string;
    erasable?: boolean;
    value: string;
    focused?: boolean;
    required?: boolean;
    closeText: string;
    onClose(): void;
    onChange(val: any): void;
    onFocus(): void;
    onAddressChange(val: any): void;
}

export const AddressAutoComplete = (props: IAddressAutoCompleteProps) => {
    const [localState, localDispatch] = useReducer(reducer, initialState(props.value));

    const container = useRef<HTMLDivElement>(undefined);

    const debouncedSearchTerm = useDebounceHook(props.value, 700);

    const firstUpdate = useRef(true);

    useEffect(() => {
        if (debouncedSearchTerm && props.value && props.value.replace(/\s/, "").length > 3 && !firstUpdate.current) {
            firstUpdate.current = false;
            localDispatch(changeSearchStatusValue(true));
            triggerChange();
        }
        if (firstUpdate.current) {
            firstUpdate.current = false;
        }
    }, [debouncedSearchTerm]);

    const handleClickOutside = (event) => {
        if (container.current && !container.current.contains(event.target) && !event.target.id.includes("keyboard")) {
            localDispatch(onCloseButton());
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutside, false);
        return () => {
            document.removeEventListener('click', handleClickOutside, false);
        };
    });

    useEffect(() => {
        if (localState.focused) {
            props.onFocus();
        }
    }, [localState.focused])
    const triggerChange = () => {
        Client.getPostCanadaAddressList(
            props.value,
            props.countryCode ? props.countryCode : CountryCodes.CA,
            props.langCode
        )
            .then(response => {
                const {
                    searchTextArray,
                    addressObjectArray
                }: { searchTextArray: string[]; addressObjectArray: ParsedAddress[] } = Client.parsedResponse(
                    response,
                    props.countryCode
                );
                localDispatch(onLoadItemListFromRequest(searchTextArray, addressObjectArray));
            })
            .catch(err => {
                // tslint:disable-next-line: no-console
                console.log(err);
            });
    };

    const renderItemList = (): JSX.Element => {
        if (!localState.focused || localState.selected) {
            return null;
        } else {
            if (localState.isLoading) {
                return renderLoadingSpinner();
            } else {
                return renderLoadedList();
            }
        }
    };

    const moreAddressOptionRow = (item: string) => (
        <div className="more-addresses-container">
            <div className="text-center">{item}</div>
            <div className="right-caret">
                <img src={rightCaret} height={25} />
            </div>
        </div>
    )

    const renderLoadedList = (): JSX.Element => {
        return (
            <div className="loaded-address-container">
                <ul>
                    {localState.items.map((item, index) => {
                        let doesAnyItemMatch = false;
                        if (item !== undefined) {
                            const stringTable = item.split(" ");
                            for (const itemValue of stringTable) {
                                if (itemValue.toLowerCase().indexOf(props.value.toLowerCase())) {
                                    doesAnyItemMatch = true;
                                    break;
                                }
                            }
                        }
                        if (doesAnyItemMatch || props.value === "") {
                            return (
                                <li
                                    key={"autocomplete" + props.id + index}
                                    className="text-center"
                                    onClick={() => onItemClick(index)}
                                >
                                    <div>
                                        {
                                            localState.itemObject[index].moreAddresses
                                                ? moreAddressOptionRow(item)
                                                : <span>{item}</span>
                                        }
                                    </div>
                                </li>
                            );
                        } else {
                            return null;
                        }
                    })}
                </ul>
                <div>
                    <button
                        className="buttons-white-wider"
                        onClick={() => {
                            localDispatch(onCloseButton());
                            props.onClose();
                        }}
                    >
                        {props.closeText}
                    </button>
                </div>
            </div>
        );
    };
    const onItemClick = (index: number) => {
        if (index === null) {
            localDispatch(onItemListClick(false, false));
            return;
        }
        if (localState.itemObject[index].moreAddresses) {
            Client.getPostCanadaAddressList2(
                localState.itemObject[index].id,
                props.countryCode ? props.countryCode : CountryCodes.CA,
                props.langCode
            )
                .then(response => {
                    const {
                        searchTextArray,
                        addressObjectArray
                    }: { searchTextArray: string[]; addressObjectArray: ParsedAddress[] } = Client.parsedResponse(
                        response,
                        props.countryCode
                    );
                    localDispatch(onLoadItemListFromRequest(searchTextArray, addressObjectArray));
                })
                // tslint:disable-next-line: no-console
                .catch(err => console.log(err));

            return;
        }
        if (localState.itemObject[index]) {
            localDispatch(onItemListClick(false, false, localState.itemObject[index].streetAddress));
            props.onAddressChange(localState.itemObject[index]);
        } else {
            localDispatch(onItemListClick(false, false));
            const returnValue = {
                streetAddress: props.value,
                country: "",
                state: "",
                city: "",
                zipCode: ""
            };
            props.onAddressChange(returnValue);
        }
    };
    const renderLoadingSpinner = (): JSX.Element => {
        if (props.value) {
            return (
                <div className="loaded-address-container">
                    <ul>
                        <li className="text-center">
                            <i className="fa fa-spinner fa-spin" />
                        </li>
                    </ul>
                </div>
            );
        }
        return null;
    };

    // Render according to props received.
    let labelClass = props.required ? "input-label required" : "input-label";
    let inputClassName = props.isValid ? "input-box" : "input-box-invalid";
    if (props.focused) {
        inputClassName = inputClassName + " focused";
    }
    if (props.required) {
        labelClass = labelClass + " required";
    }
    return (
        <div style={{ position: "relative" }} ref={container}>
            <input
                className={inputClassName}
                onFocus={(e) => {e.stopPropagation(); localDispatch(onAddressInputFocus())}}
                style={{ textAlign: "left" }}
                placeholder={props.placeholder}
                onChange={e => props.onChange(e.target.value)}
                value={props.value}
                onClick={(e)=> e.stopPropagation()}
            />
            {renderItemList()}
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/components/AddressAutoComplete/AddressAutoComplete.tsx